import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Save, Car, DollarSign, TrendingUp, Clock } from "lucide-react";
import API from "../../api/axiosConfig";

const STATUS_LABELS = {
  buscando:   { label: "Buscando",   color: "bg-yellow-100 text-yellow-700" },
  aceptado:   { label: "Aceptado",   color: "bg-blue-100   text-blue-700"   },
  recogiendo: { label: "Recogiendo", color: "bg-purple-100 text-purple-700" },
  en_viaje:   { label: "En viaje",   color: "bg-indigo-100 text-indigo-700" },
  completado: { label: "Completado", color: "bg-green-100  text-green-700"  },
  cancelado:  { label: "Cancelado",  color: "bg-red-100    text-red-700"    },
};

export default function TransportAdmin() {
  const [pricing, setPricing]         = useState(null);
  const [form, setForm]               = useState({});
  const [saving, setSaving]           = useState(false);
  const [previewKm, setPreviewKm]     = useState(5);

  // ── Cargar configuración ───────────────────────────────────────────────
  useEffect(() => {
    API.get("/transport/pricing")
      .then((res) => {
        const p = res.data?.data?.pricing;
        setPricing(p);
        setForm({
          baseFare:        Number(p.baseFare),
          pricePerKm:      Number(p.pricePerKm),
          pricePerMin:     Number(p.pricePerMin),
          minimumFare:     Number(p.minimumFare),
          roadFactor:      Number(p.roadFactor),
          surgeMultiplier: Number(p.surgeMultiplier),
          name:            p.name,
        });
      })
      .catch(() => toast.error("Error al cargar configuración de tarifas"));
  }, []);


  // ── Guardar configuración ──────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.put("/transport/pricing", form);
      setPricing(res.data?.data?.pricing);
      toast.success("Tarifas de transporte actualizadas");
    } catch {
      toast.error("Error al guardar las tarifas");
    } finally {
      setSaving(false);
    }
  };

  // ── Previsualización de tarifa ─────────────────────────────────────────
  const previewFare = () => {
    if (!form.baseFare) return 0;
    const roadKm = previewKm * (form.roadFactor || 1.3);
    const durationMin = (previewKm / 25) * 60;
    const raw =
      form.baseFare +
      roadKm * (form.pricePerKm || 800) +
      durationMin * (form.pricePerMin || 100);
    const fare = Math.max(form.minimumFare || 4000, raw) * (form.surgeMultiplier || 1);
    return Math.ceil(fare / 100) * 100;
  };

  const field = (label, key, step = 1, help = "") => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="number"
        step={step}
        value={form[key] ?? ""}
        onChange={(e) => setForm((f) => ({ ...f, [key]: Number(e.target.value) }))}
        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-supernova-400 text-sm"
      />
      {help && <p className="text-xs text-gray-400 mt-1">{help}</p>}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <Car className="text-supernova-500" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Configuración de Transporte</h1>
          <p className="text-sm text-gray-500">Tarifas para el módulo de viajes Uber-style</p>
        </div>
      </div>

      {/* ── Configuración de tarifas ── */}
      <div className="bg-white rounded-2xl shadow p-6">
        <h2 className="text-lg font-bold text-gray-700 mb-5 flex items-center gap-2">
          <DollarSign size={20} className="text-supernova-500" />
          Tarifas
        </h2>

        {pricing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {field("Nombre del plan", "name", 1)}
            {field("Tarifa base (COP)", "baseFare", 100, "Cobro fijo al inicio del viaje")}
            {field("Precio por km (COP)", "pricePerKm", 50, "Costo adicional por cada kilómetro")}
            {field("Precio por minuto (COP)", "pricePerMin", 10, "Costo adicional por cada minuto de viaje")}
            {field("Tarifa mínima (COP)", "minimumFare", 100, "Precio mínimo garantizado por viaje")}
            {field("Factor de ruta", "roadFactor", 0.01, "Multiplicador para calcular km reales (1.30 = +30% por curvas)")}
            {field("Multiplicador de demanda", "surgeMultiplier", 0.1, "1.0 = normal, 1.5 = alta demanda")}
          </div>
        ) : (
          <div className="text-center text-gray-400 py-6 animate-pulse">Cargando tarifas...</div>
        )}

        {pricing && (
          <div className="mt-6 flex flex-col sm:flex-row gap-4 items-start">
            {/* Previsualización */}
            <div className="flex-1 bg-supernova-50 rounded-xl p-4">
              <p className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
                <TrendingUp size={14} /> Previsualización de tarifa
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={30}
                  value={previewKm}
                  onChange={(e) => setPreviewKm(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-gray-700 w-12">{previewKm} km</span>
              </div>
              <p className="text-supernova-600 font-bold text-xl mt-2">
                ${previewFare().toLocaleString("es-CO")}
              </p>
            </div>

            {/* Guardar */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="sm:self-end flex items-center gap-2 px-6 py-3 bg-supernova-500 text-white rounded-xl font-bold hover:opacity-90 disabled:opacity-50 transition"
            >
              <Save size={18} />
              {saving ? "Guardando..." : "Guardar tarifas"}
            </button>
          </div>
        )}
      </div>

      {/* ── Resumen de fórmula ── */}
      {pricing && (
        <div className="bg-gray-50 rounded-2xl p-4 text-sm text-gray-600">
          <p className="font-medium mb-1">Fórmula de tarifa:</p>
          <code className="text-xs bg-white rounded-lg px-3 py-2 block border">
            fare = max(minimumFare, baseFare + (km × roadFactor × pricePerKm) + (min × pricePerMin)) × surgeMultiplier
          </code>
          <p className="mt-2 text-xs text-gray-400">
            El resultado se redondea a la centena superior. Ejemplo a 5 km:{" "}
            <strong className="text-supernova-600">${previewFare().toLocaleString("es-CO")}</strong>
          </p>
        </div>
      )}

      {/* ── Instrucción para ver historial ── */}
      <div className="bg-white rounded-2xl shadow p-6">
        <h2 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
          <Clock size={20} className="text-supernova-500" />
          Historial de viajes
        </h2>
        <p className="text-sm text-gray-500">
          Los conductores y pasajeros pueden consultar su historial de viajes desde sus respectivos dashboards
          en <strong>/transport</strong> (usuario) y <strong>/transport/conductor</strong> (conductor).
        </p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <div key={k} className={`rounded-xl px-3 py-2 text-xs font-medium ${v.color}`}>
              {v.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
